/** Главная. Ответ на «сколько ещё можно» — и всё, что нужно, чтобы этому числу
 *  верить: куда ушёл месяц, что записалось последним, сколько у меня всего.
 *
 * Порядок блоков — порядок вопросов (спека §3.1): сколько осталось → записать →
 * куда уходит → что записалось → сколько съедают подписки. Каждый блок грузится
 * сам за себя: упавший пирог не должен уносить лимит с экрана.
 */
import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type Allowance, type Capital, type Failure, type Operation,
         type MonthReview, type Summary } from "../lib/api.ts";
import { FindingCard } from "../components/FindingCard.tsx";
import { LimitCard } from "../components/LimitCard.tsx";
import { LimitSheet } from "../components/LimitSheet.tsx";
import { MonthReviewSheet } from "../components/MonthReviewSheet.tsx";
import { CategoryIcon } from "../components/CategoryIcon.tsx";
import { Donut } from "../components/Donut.tsx";
import { Fallback } from "./Fallback.tsx";
import { RecordInput } from "../components/RecordInput.tsx";
import { SnapshotSheet } from "./Capital.tsx";
import { colorize, type Colored } from "../lib/category.ts";
import { formatMoney } from "../lib/money.ts";
import { monthIn, monthName } from "../lib/group.ts";
import { plural } from "../lib/plural.ts";
import { tap } from "../lib/telegram.ts";
import type { Tab } from "../components/TabBar.tsx";

/** Сколько операций показываем последними. Пять — столько влезает на экран, не
 *  отодвигая подписки за сгиб. */
const RECENT = 5;

export function Home({ onOpen, onTab }: {
  onOpen: (page: "subscriptions" | "settings") => void;
  onTab: (tab: Tab) => void;
}) {
  const [state, setState] = useState<Allowance | null>(null);
  const [month, setMonth] = useState<Summary | null>(null);
  const [recent, setRecent] = useState<Operation[] | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [loading, setLoading] = useState(true);
  const [settingLimit, setSettingLimit] = useState(false);
  // Часть блоков не догрузилась. Не `failure`: лимит на месте, гасить экран не
  // за что — но и делать вид, что разбора просто нет, нельзя.
  const [partial, setPartial] = useState(false);
  const [snapping, setSnapping] = useState(false);
  // Капитал держим здесь: он нужен и уголку героя, и шиту снимка — иначе шит
  // открылся бы пустой формой у человека, у которого кошельки давно заведены.
  const [capital, setCapital] = useState<Capital | null>(null);
  const [review, setReview] = useState<MonthReview | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  // «Трата» — не отдельный экран, а то же поле ввода: оно и так стоит выше, и
  // заводить под него шит значило бы добавить шаг к самому частому действию.
  // Кнопка нужна, когда страница домотана вниз и поля не видно.
  const focusInput = () => {
    const field = document.querySelector<HTMLInputElement>('input[aria-label="Записать трату фразой"]');
    field?.scrollIntoView({ block: "center", behavior: "smooth" });
    field?.focus();
  };

  const load = useCallback(() => {
    setLoading(true);
    setFailure(null);
    api.allowance()
      .then(setState)
      .catch((e) => setFailure(e instanceof ApiError ? e.kind : "broken"))
      .finally(() => setLoading(false));
    // Пирог и лента — не критичные: гасить из-за них весь экран хуже, чем
    // показать лимит без разбора. Но упавший запрос и «данных ещё нет» — разные
    // вещи, а раньше оба давали пустоту: блок просто исчезал молча. Теперь
    // отказ виден строкой, и её можно нажать.
    setPartial(false);
    api.summary("month").then(setMonth).catch(() => { setMonth(null); setPartial(true); });
    // Просим ровно пять: раньше приходил весь месяц, и 199 строк из 200
    // выбрасывались на клиенте.
    api.operations("month", { limit: RECENT }).then((r) => setRecent(r.items))
       .catch(() => { setRecent(null); setPartial(true); });
    api.monthReview().then(setReview).catch(() => setReview(null));
    api.capital().then(setCapital).catch(() => { setCapital(null); setPartial(true); });
  }, []);

  useEffect(load, [load]);

  if (failure) return <Fallback kind={failure} onRetry={load} />;

  const slices = month ? colorize(month.categories) : [];

  return (
    <div style={{ padding: "var(--screen)" }}>
      {/* Заголовок нужен навигации по заголовкам, а на экране он лишний:
          главное число и так говорит само за себя. */}
      <h1 style={sr}>Сколько можно тратить</h1>
      {loading ? <Skeleton /> : (
        <LimitCard state={state} onSet={() => setSettingLimit(true)}
                   corner={<CapitalChip capital={capital} onOpen={() => onTab("capital")} />} />
      )}
      {/* Находка стоит СРАЗУ под главным числом и выше действий: она про то,
          что уже случилось с деньгами, и заметить её надо до того, как человек
          пойдёт записывать следующую трату. Своего места на экране не занимает —
          когда сказать нечего, компонент не рисует ничего. */}
      <FindingCard onActed={load} onRecord={focusInput} />
      {/* Записал — лимит обязан обновиться сразу: иначе экран покажет число,
          которое человек только что своими руками изменил. */}
      <RecordInput onRecorded={load} />
      {/* MA-FR-3: ровно три действия — на узкий экран больше не помещается. */}
      <ActionRow onFocusInput={focusInput} onSnapshot={() => setSnapping(true)}
                 onSubscriptions={() => onOpen("subscriptions")} />
      <MonthReviewRow review={review} onOpen={() => setReviewOpen(true)} />
      <MonthCard month={month} slices={slices} onOpen={() => onTab("ops")} />
      <RecentOps rows={recent} slices={slices} onOpen={() => onTab("ops")} />
      {partial && (
        <button type="button" onClick={() => { tap(); load(); }}
          style={{ display: "flex", alignItems: "center", gap: "var(--s-3)", width: "100%",
                   marginTop: "var(--s-3)", padding: "var(--pad-control) var(--s-4)",
                   background: "var(--surface)", borderRadius: "var(--r-lg)", border: "none",
                   font: "inherit", color: "inherit", cursor: "pointer", textAlign: "left",
                   minHeight: "var(--tap)" }}>
          <span style={{ flexGrow: 1, fontSize: "var(--f-body-s)",
                         color: "var(--text-muted)" }}>
            Часть блоков не загрузилась
          </span>
          <span style={{ fontSize: "var(--f-caption)", color: "var(--accent)" }}>Ещё раз</span>
        </button>
      )}
      <SettingsRow onOpen={() => onOpen("settings")} />
      {reviewOpen && review && (
        <MonthReviewSheet review={review} monthName={monthName(review.month)}
                          onClose={() => { dismissReview(review.month); setReviewOpen(false);
                                           setReview(null); }}
                          onApplied={() => { dismissReview(review.month); setReviewOpen(false);
                                             setReview(null); load(); }} />
      )}
      {snapping && (
        <SnapshotSheet previous={capital?.pots ?? []} onClose={() => setSnapping(false)}
                       onDone={() => { setSnapping(false); load(); }} />
      )}
      {settingLimit && (
        <LimitSheet state={state} onClose={() => setSettingLimit(false)}
                    onDone={() => { setSettingLimit(false); load(); }} />
      )}
    </div>
  );
}

/** Три самых частых действия (MA-FR-3).
 *
 * «Трата» ведёт в поле ввода, а не в отдельный шит: поле стоит тут же, и
 * лишний экран под самое частое действие — это шаг, который делают каждый день.
 *
 * Отдельной строки подписок больше нет: она вела туда же, куда эта кнопка, и
 * держать два входа в одно место на одном экране незачем. Месячный итог, ради
 * которого строка была нужна, переехал подписью под кнопкой.
 */
function ActionRow({ onFocusInput, onSnapshot, onSubscriptions }: {
  onFocusInput: () => void; onSnapshot: () => void; onSubscriptions: () => void;
}) {
  const [subs, setSubs] = useState<{ sum: string; currency: string } | null>(null);
  useEffect(() => {
    api.subscriptions()
      .then((s) => (s.active.length > 0
        ? setSubs({ sum: s.monthly_total, currency: s.base_currency })
        : setSubs(null)))
      .catch(() => undefined);   // не пришло — просто нет подписи
  }, []);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "var(--s-2)",
                  marginTop: "var(--s-3)" }}>
      <Action label="Трата" onPick={onFocusInput}
              paths={["M12 5v14M5 12h14"]} />
      {/* Не «Снимок»: рядом с фотоаппаратом читалось как «сфотографировать», а
          у бота есть разбор фото чеков — путаница дорогая. И не «Остатки»: в
          герое прямо над кнопкой стоит «Осталось до конца месяца» про лимит, и
          два однокоренных слова о разных числах спорят друг с другом.
          «Кошельки» — термин из глоссария, содержимое шторки и значок разом. */}
      <Action label="Кошельки" onPick={onSnapshot}
              paths={["M3.5 8.5A2 2 0 015.5 6.5h13a2 2 0 012 2v9a2 2 0 01-2 2h-13a2 2 0 01-2-2z",
                      "M16.5 13h2.7",
                      "M6.5 6.5V5a1.5 1.5 0 011.9-1.4l8.6 2.3"]} />
      <Action label="Подписки" onPick={onSubscriptions}
              note={subs ? formatMoney(subs.sum, subs.currency) : undefined}
              paths={["M20.5 11.5A8.5 8.5 0 006.2 5.8L3.5 8.4", "M3.5 4v4.4h4.4",
                      "M3.5 12.5a8.5 8.5 0 0014.3 5.7l2.7-2.6", "M20.5 20v-4.4h-4.4"]} />
    </div>
  );
}

function Action({ label, paths, note, onPick }: {
  label: string; paths: string[]; note?: string; onPick: () => void;
}) {
  return (
    <button type="button" onClick={() => { tap(); onPick(); }}
      // justifyContent обязателен: карточки в ряду тянутся по высоте самой
      // высокой (у «Подписок» есть третья строка с суммой), и без него
      // содержимое коротких прижималось к верху — ряд выглядел кривым.
      style={{ display: "flex", flexDirection: "column",
               alignItems: "center", justifyContent: "center", gap: 5,
               background: "var(--surface)", borderRadius: "var(--r-lg)",
               padding: "12px 6px", border: "none", font: "inherit", color: "inherit",
               cursor: "pointer", minHeight: "var(--tap)" }}>
      <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="var(--accent)"
           strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {paths.map((d) => <path key={d} d={d} />)}
      </svg>
      <span style={{ fontSize: "var(--f-caption)" }}>{label}</span>
      {/* Третья строка есть всегда, даже пустая: без неё карточка с суммой была
          на строку выше соседей, и значки в ряду стояли на разной высоте. */}
      <span className="num" aria-hidden={note ? undefined : "true"}
            style={{ fontSize: "var(--f-caption-s)", color: "var(--text-muted)",
                     // Межстрочную задаём явно: у пустой строки и строки с
                     // текстом высота иначе расходится на пиксель, и значки в
                     // ряду стоят чуть по-разному.
                     lineHeight: 1.3, minHeight: "1.3em" }}>{note ?? "\u00A0"}</span>
    </button>
  );
}

/** Итог закрытого месяца — строкой, а не всплывающим шитом.
 *
 * Шит, открывающийся сам при запуске, — это перехват управления: человек зашёл
 * записать кофе, а его спрашивают про лимит. Строка ждёт, пока её нажмут.
 *
 * Показывается только пока итог свежий (первая неделя месяца, считает сервер) и
 * только если есть что предложить. Иначе это не новость, а украшение.
 */
function MonthReviewRow({ review, onOpen }: {
  review: MonthReview | null; onOpen: () => void;
}) {
  if (!review || !review.is_fresh || review.proposal === null) return null;
  if (isDismissed(review.month)) return null;
  const cut = Number(review.proposal) < Number(review.limit);
  return (
    <button type="button" onClick={() => { tap(); onOpen(); }}
      style={{ display: "flex", alignItems: "center", gap: "var(--s-3)", width: "100%",
               background: "var(--accent-tint)", borderRadius: "var(--r-lg)",
               padding: "14px 16px", marginTop: "var(--s-3)", border: "none",
               font: "inherit", color: "inherit", cursor: "pointer", textAlign: "left",
               minHeight: "var(--tap)" }}>
      <span style={{ flexGrow: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: "var(--f-body-s)", fontWeight: 500 }}>
          Итог: {monthName(review.month)}
        </span>
        <span style={{ display: "block", fontSize: "var(--f-caption)",
                       color: "var(--text-muted)", marginTop: 2 }}>
          {cut ? "лимит можно опустить" : "лимит можно поднять"}
        </span>
      </span>
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--text-disabled)"
           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 6l6 6-6 6" />
      </svg>
    </button>
  );
}

/** Отложенный итог помним в браузере: месяц в ключе, и на следующий месяц
 *  предложение придёт снова само. Хранилище может быть недоступно (приватный
 *  режим, чистка) — тогда строка просто покажется ещё раз, и это не поломка. */
const DISMISS_KEY = "month-review-dismissed";

function isDismissed(month: string): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === month;
  } catch {
    return false;
  }
}

function dismissReview(month: string): void {
  try {
    localStorage.setItem(DISMISS_KEY, month);
  } catch {
    // Не сохранилось — покажем ещё раз. Терять из-за этого нечего.
  }
}

/** MA-FR-2. Капитал компактно в углу героя: последний снимок одним числом.
 *  Снимков ещё нет — угол пустой, а не «0 $»: нуля у человека нет. */
function CapitalChip({ capital, onOpen }: {
  capital: Capital | null; onOpen: () => void;
}) {
  if (!capital) return null;
  return (
    <button type="button" onClick={() => { tap(); onOpen(); }}
      style={{ display: "flex", gap: 6, flexShrink: 0,
               alignItems: "baseline", background: "var(--surface-2)", border: "none",
               borderRadius: "var(--r-pill)", padding: "6px 11px", font: "inherit",
               color: "inherit", cursor: "pointer", minHeight: 32 }}>
      <span style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)" }}>Капитал</span>
      <span className="num" style={{ fontSize: "var(--f-caption)", fontWeight: 600 }}>
        {formatMoney(capital.total, capital.base_currency)}
      </span>
    </button>
  );
}

/** Куда ушёл месяц. Пончик и три верхние категории — дальше глаз всё равно
 *  считает, а не смотрит; за остальным экран «Операции». */
function MonthCard({ month, slices, onOpen }: {
  month: Summary | null; slices: Colored[]; onOpen: () => void;
}) {
  if (!month || slices.length === 0) return null;
  return (
    <button type="button" onClick={() => { tap(); onOpen(); }} style={{ ...cardButton }}>
      <div style={{ display: "flex", alignItems: "baseline", width: "100%" }}>
        <span style={{ flexGrow: 1, fontSize: "var(--f-t3)", fontWeight: 600 }}>
          Траты в {monthIn(month.start)}
        </span>
        <span style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)" }}>
          {/* Считаем РАСХОДНЫЕ записи: карточка про траты, и общий счётчик
              приписывал ей зарплаты — «5 операций» при двух тратах. */}
          {plural(month.expense_count, ["операция", "операции", "операций"])}
        </span>
        <Chevron />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--s-4)",
                    width: "100%", marginTop: "var(--s-3)" }}>
        <div style={{ flexGrow: 1, minWidth: 0, display: "flex", flexDirection: "column",
                      gap: 9 }}>
          {slices.slice(0, 3).map((s) => (
            <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                             background: s.color }} />
              <span style={{ flexGrow: 1, minWidth: 0, overflow: "hidden",
                             textOverflow: "ellipsis", whiteSpace: "nowrap",
                             fontSize: "var(--f-body-s)" }}>{s.name}</span>
              <span className="num" style={{ fontSize: "var(--f-body-s)",
                                             color: "var(--text-muted)" }}>
                {formatMoney(s.amount, month.base_currency)}
              </span>
            </div>
          ))}
        </div>
        <div style={{ width: 118, flexShrink: 0 }}>
          <Donut slices={slices} total={month.expense_total}
                 currency={month.base_currency} compact />
        </div>
      </div>
    </button>
  );
}

/** Последние записи. Не для анализа, а чтобы увидеть: то, что я сейчас сказал,
 *  действительно записалось — и записалось так, как я имел в виду. */
function RecentOps({ rows, slices, onOpen }: {
  rows: Operation[] | null; slices: Colored[]; onOpen: () => void;
}) {
  if (!rows || rows.length === 0) return null;
  const colorOf = (name: string) =>
    slices.find((s) => s.name === name)?.color ?? "var(--cat-rest)";
  return (
    <button type="button" onClick={() => { tap(); onOpen(); }} style={{ ...cardButton }}>
      <div style={{ display: "flex", alignItems: "baseline", width: "100%" }}>
        <span style={{ flexGrow: 1, fontSize: "var(--f-t3)", fontWeight: 600 }}>
          Последние операции
        </span>
        <span style={{ fontSize: "var(--f-caption)", color: "var(--accent)" }}>Все</span>
        <Chevron />
      </div>
      <div style={{ width: "100%", marginTop: "var(--s-2)" }}>
        {rows.map((row) => (
          <div key={row.id} style={{ display: "flex", alignItems: "center", gap: "var(--s-3)",
                                     padding: "13px 0" }}>
            <CategoryIcon name={row.category_name} color={colorOf(row.category_name)} size={34} />
            <span style={{ flexGrow: 1, minWidth: 0, overflow: "hidden",
                           textOverflow: "ellipsis", whiteSpace: "nowrap",
                           fontSize: "var(--f-body-s)" }}>
              {row.description || row.category_name}
            </span>
            <span className="num" style={{ fontSize: "var(--f-body-s)", fontWeight: 500,
                    color: row.op_type === "income" ? "var(--income)" : "var(--text)" }}>
              {/* Минус у расхода обязателен: на экране «Операции» та же запись
                  показана как «−20 $», и без знака здесь одна и та же трата
                  выглядит в приложении по-разному. */}
              {row.op_type === "income" ? "+" : "−"}
              {formatMoney(row.amount, row.currency)}
            </span>
          </div>
        ))}
      </div>
    </button>
  );
}

/** Скелетон формы того, что сейчас появится. Спиннера нет: крутилка не говорит,
 *  чего ждать, а прямоугольник нужного размера не даёт экрану прыгнуть. */
function Skeleton() {
  return (
    <div style={{ background: "var(--surface)", borderRadius: "var(--r-lg)", padding: "var(--s-5)" }}
         role="status" aria-busy="true" aria-label="Загружаю лимит">
      <div style={{ height: 11, width: "48%", background: "var(--surface-3)", borderRadius: 5 }} />
      <div style={{ height: 30, width: "38%", background: "var(--surface-3)", borderRadius: 7,
                    marginTop: 10 }} />
      <div style={{ height: 9, width: "60%", background: "var(--surface-2)", borderRadius: 5,
                    marginTop: 10 }} />
      <div style={{ height: 6, background: "var(--surface-2)", borderRadius: 3,
                    marginTop: "var(--s-4)" }} />
    </div>
  );
}

function SettingsRow({ onOpen }: { onOpen: () => void }) {
  return (
    <button type="button" onClick={() => { tap(); onOpen(); }} style={row}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)"
           strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="3.2" />
        <path d="M19.4 15a1.7 1.7 0 00.3 1.9l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.9-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1A1.7 1.7 0 008.9 19a1.7 1.7 0 00-1.9.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.9 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1A1.7 1.7 0 005 8.9a1.7 1.7 0 00-.3-1.9l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.9.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.9-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.9V10a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" />
      </svg>
      <span style={{ flexGrow: 1, fontSize: "var(--f-body)" }}>Настройки</span>
      <Chevron />
    </button>
  );
}

function Chevron() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--text-disabled)"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
         style={{ flexShrink: 0, marginLeft: 4 }}>
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

const row: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: "var(--s-3)", width: "100%",
  background: "var(--surface)", borderRadius: "var(--r-lg)", padding: "14px 16px",
  marginTop: "var(--s-3)", border: "none", font: "inherit", color: "inherit",
  cursor: "pointer", textAlign: "left", minHeight: "var(--tap)",
};

const cardButton: React.CSSProperties = {
  display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%",
  background: "var(--surface)", borderRadius: "var(--r-lg)", padding: "16px",
  marginTop: "var(--s-3)", border: "none", font: "inherit", color: "inherit",
  cursor: "pointer", textAlign: "left",
};

const sr: React.CSSProperties = {
  position: "absolute", width: 1, height: 1, overflow: "hidden",
  clip: "rect(0 0 0 0)", whiteSpace: "nowrap",
};
